import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { promoCodesHelpUrl } from 'utilities/urlUtils'
import { CheckIcon } from '../icons'
import HeroAnimation from './HeroAnimation'

const t = buildTranslate('promo_codes.overview.hero')
const tOverview = buildTranslate('promo_codes.overview')
const tLanding = buildTranslate('promo_codes.landing')

const POINT_KEYS = ['acquisition', 'everywhere', 'measurable'] as const

const CHECK_SIZE = 14
const POINT_LINE_HEIGHT = '24px'
const COPY_COLUMN = 668
const VISUAL_COLUMN = 512
const STACK_BREAKPOINT = 992

type Props = {
  onCreateClick: () => void
}

const Hero = ({ onCreateClick }: Props) => {
  const [css] = useStyletron()

  return (
    <section
      data-testid="promo-codes-hero"
      className={css({
        display: 'grid',
        gridTemplateColumns: '1fr',
        // Figma splits the hero 668 / 512. auto-fit would force 50/50, which
        // leaves the copy cramped and the animation column over-wide.
        [`@media (min-width: ${STACK_BREAKPOINT}px)`]: {
          gridTemplateColumns: `${COPY_COLUMN}fr ${VISUAL_COLUMN}fr`,
        },
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey20}`,
        borderRadius: '8px',
        overflow: 'hidden',
      })}
    >
      <div className="p-xl">
        <Text.SmallCaps3 bold color="primary" tag="div" className="mb-xs">
          {t('eyebrow')}
        </Text.SmallCaps3>
        <Text.Header2 tag="h1" color="grey90" className="mt-0 mb-s">
          {t('title')}
        </Text.Header2>
        <Text.BodyText3 color="grey70" tag="p" className="mb-l">
          {t('body_prefix')}
          <Text.BodyText3 bold color="grey90" tag="span">
            {t('body_emphasis')}
          </Text.BodyText3>
          {t('body_suffix')}
        </Text.BodyText3>

        <ul
          className={`mb-l ${css({
            listStyle: 'none',
            padding: 0,
            margin: 0,
          })}`}
        >
          {POINT_KEYS.map(key => (
            <li
              key={key}
              className={`mb-s ${css({
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              })}`}
            >
              <span
                className={css({
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  height: POINT_LINE_HEIGHT,
                })}
              >
                <CheckIcon size={CHECK_SIZE} />
              </span>
              <Text.BodyText3
                color="grey70"
                tag="span"
                className={css({ lineHeight: POINT_LINE_HEIGHT })}
              >
                <Text.BodyText3 bold color="grey90" tag="span">
                  {t(`points.${key}.lead`)}
                </Text.BodyText3>{' '}
                {t(`points.${key}.rest`)}
              </Text.BodyText3>
            </li>
          ))}
        </ul>

        <div
          className={css({ display: 'flex', gap: '12px', flexWrap: 'wrap' })}
        >
          <Button onClick={onCreateClick} data-testid="hero-create-promo-code">
            {tLanding('create_promo_code')}
          </Button>
          <Button
            kind={Kind.SECONDARY}
            aria-label={`${t('learn_more')} ${tOverview(
              'opens_in_new_window'
            )}`}
            onClick={() => window.open(promoCodesHelpUrl, '_blank', 'noopener')}
          >
            {t('learn_more')}
          </Button>
        </div>
      </div>

      <HeroAnimation />
    </section>
  )
}

export default Hero
